<ng-container *ngIf="!(loading$ | async); else loading">
  <h1 class="card-title">
    {{ ("jobSeries" | i18n) || "Job Series" | titlecase }}
    <span *ngIf="department$ | async as department">
      - {{ department.name }}
    </span>
  </h1>

  <div class="jobs-wrapper">
    <div class="jobs-list">
      <app-accordion
        *ngFor="
          let summary of multiDayJobSummaries$ | async;
          trackBy: trackByJobId
        "
        class="multiday-edit-job-item"
        [attr.data-cy]="'multiday-job-' + summary.job._id"
        [collapsed]="summary.collapsed"
        (accordionToggled)="toggleJobCollapsed(summary, $event)">
        <app-accordion-header
          [collapseOnHeaderClick]="true"
          [displayArrowButton]="true"
          [newCollapseButton]="true"
          [attr.data-cy]="
            'multiday-job-header-' +
            summary.job._id +
            (!summary.collapsed ? '-open' : '')
          "
          class="multiday-edit-job-header"
          [class.is-canceled]="summary.job.status === jobStatusEnum.CANCELED"
          [class.available]="summary.jobCounts.available > 0"
          [class.booked]="
            summary.jobCounts.booked >= summary.jobCounts.requiredUsers
          "
          [class.action-required]="
            summary.jobCounts.open === 0 &&
            summary.jobCounts.booked < summary.jobCounts.requiredUsers
          "
          [class.collapsed]="summary.collapsed">
          <div
            class="status-bar"
            [attr.data-cy]="'status-bar-' + summary.job.status"
            [matTooltip]="getStatusTooltip(summary)"
            [matTooltipShowDelay]="100"
            matTooltipPosition="above"
            [class.status-action-required]="
              summary.jobCounts.open === 0 &&
              summary.jobCounts.booked < summary.jobCounts.requiredUsers
            "
            [class.status-closed]="
              summary.jobCounts.booked >= summary.jobCounts.requiredUsers
            "
            [class.status-has-available-invitees]="
              summary.jobCounts.available > 0
            "></div>

          <div class="header-meta">
            <app-job-list-item-counts
              [hidePotentialInviteeCount]="
                summary.job.status !== jobStatusEnum.OPEN
              "
              [counts]="summary.jobCounts"></app-job-list-item-counts>
            <app-job-list-item-date-time
              [withDate]="true"
              [job]="summary.job"></app-job-list-item-date-time>
            <div class="flex-grow"></div>
          </div>
        </app-accordion-header>

        <app-accordion-content #accordionContent>
          <div
            *ngIf="!(accordionContent.contentCompletelyHidden$ | async)"
            class="multiday-edit-job-content">
            <!-- Invitees list section -->
            <div
              *ngIf="summary.overviewInvitees.length > 0"
              class="invitees-preview-wrapper">
              <div class="invitees-header">
                <h4>
                  {{ "requestedEmployees" | i18n | titlecase }}
                </h4>
              </div>
              <app-job-invitee-list
                [job]="summary.job"
                [invitees]="summary.overviewInvitees"
                [condensed]="true"
                [jobCustomer]="summary.jobCustomer"
                [loggedInUser]="(loggedInUser$ | async) || undefined"
                [userCounts]="(userCount$ | async) || undefined"
                [totalCount]="(preparedJobs$ | async)?.length"
                (click)="$event.stopPropagation()"
                [displayHeader]="false"
                data-cy="multiday-job-invitees">
              </app-job-invitee-list>
            </div>
            <div
              *ngIf="
                summary.possibleUsers.length === 0 &&
                summary.job.status === jobStatusEnum.OPEN
              "
              class="no-available-users-message"
              data-cy="no-available-users-message">
              {{ "noUsersFound" | i18n }}
            </div>
            <div
              *ngIf="summary.possibleUsers.length > 0"
              class="possible-users-preview-wrapper">
              <div class="possible-users-header">
                <h4>
                  {{ "availableEmployees" | i18n | titlecase }}
                </h4>
              </div>
              <app-job-possible-users-list
                [collection]="summary.possibleUsers"
                [job]="summary.job"
                [jobDate]="summary.job.date"
                [directInvitedUsers]="summary.directInvitedUsers"
                [directAcceptedUsers]="summary.directAcceptedUsers"
                (inviteeToggled)="onInviteeToggled($event, summary.job)"
                [suggestInsteadDirectAccept]="
                  summary.suggestInsteadDirectAccept
                "
                [jobCount]="(preparedJobs$ | async)?.length"
                [userCount]="(userCount$ | async) || undefined"
                [jobCustomer]="summary.jobCustomer"
                [loggedInCustomer]="(loggedInCustomer$ | async) || undefined"
                [jobInPast]="summary.jobInPast"
                (selectAllToggled)="
                  onSelectAllToggled($event, summary.job, summary.possibleUsers)
                "
                (click)="$event.stopPropagation()"
                data-cy="multiday-job-possible-users">
              </app-job-possible-users-list>
            </div>

            <div class="job-footer">
              <div class="start-items">
                <div
                  *ngIf="summary.job?.orderId"
                  class="order-id"
                  data-cy="job-order-id"
                  [matTooltip]="'orderId' | i18n">
                  #{{ summary.job.orderId }}
                </div>
              </div>
              <div class="end-items">
                <button
                  class="outline-pill-button icon-left"
                  type="button"
                  (click)="editSingleJob(summary.job._id)"
                  [attr.data-cy]="'edit-job-' + summary.job._id">
                  <mat-icon svgIcon="pencil-outline"></mat-icon>
                  <span class="mat-fab__label">
                    {{ ("edit" | i18n) || "Edit" | titlecase }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </app-accordion-content>
      </app-accordion>
    </div>
  </div>

  <div class="floating-actions-bar">
    <button
      *ngIf="hasSelections$ | async"
      class="primary-pill-button icon-left big-fab"
      (click)="submitSelection()"
      type="button"
      data-cy="submit-selection-button">
      <mat-icon svgIcon="send"></mat-icon>
      <span class="mat-fab__label">
        <ng-container *ngIf="selectionCounts$ | async as counts">
          <ng-container
            *ngIf="counts.inviteCount > 0 && counts.bookCount === 0">
            <span data-cy="invite-count">{{ counts.inviteCount }} </span>
            {{ ("invite" | i18n) || "einladen" | titlecase }}
          </ng-container>
          <ng-container
            *ngIf="counts.inviteCount === 0 && counts.bookCount > 0">
            <span data-cy="book-count">{{ counts.bookCount }} </span>
            {{ ("book" | i18n) || "buchen" | titlecase }}
          </ng-container>
          <ng-container *ngIf="counts.inviteCount > 0 && counts.bookCount > 0">
            <span data-cy="invite-and-book-count">
              {{
                ("inviteAndBook"
                  | i18n
                    : {
                        inviteCount: counts.inviteCount,
                        bookCount: counts.bookCount
                      }) ||
                  counts.inviteCount +
                    " einladen und " +
                    counts.bookCount +
                    " buchen" | titlecase
              }}
            </span>
          </ng-container>
        </ng-container>
      </span>
    </button>

    <button
      *ngIf="hasSelections$ | async"
      class="outline-pill-button icon-left big-fab"
      (click)="clearAllSelections()"
      type="button"
      data-cy="clear-selections-button">
      <mat-icon svgIcon="close"></mat-icon>
      <span class="mat-fab__label">
        {{ "clearSelection" | i18n | titlecase }}
      </span>
    </button>

    <button
      class="secondary-pill-button icon-left big-fab"
      (click)="goBackToJobs()"
      type="button"
      data-cy="back-to-jobs-button">
      <mat-icon svgIcon="arrow-left"></mat-icon>
      <span class="mat-fab__label">
        {{ ("back" | i18n) || "Back" | titlecase }}
      </span>
    </button>
  </div>
</ng-container>
<ng-template #loading>
  <div class="loading-container">
    <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
    <p>{{ ("loading" | i18n) || "Loading..." }}</p>
  </div>
</ng-template>
